import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as riskService from '../services/withdrawal-target-risk.service';
import type {
  WithdrawalTargetRiskDTO,
  WithdrawalTargetRiskUserDTO,
} from '../types/withdrawal-target-risk.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'admin role required': '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid user id': '用户 ID 无效',
      'invalid min distinct users': '关联用户数筛选无效',
      'withdrawal risk reason required': '请填写停用原因',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWithdrawalTargetRisk() {
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToastStore((s) => s.addToast);
  const targetType = searchParams.get('targetType') || '';
  const keyword = searchParams.get('keyword') || '';
  const userId = searchParams.get('userId') || '';
  const minDistinctUsers = searchParams.get('minDistinctUsers') || '';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;

  const [items, setItems] = useState<WithdrawalTargetRiskDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WithdrawalTargetRiskDTO | null>(null);
  const [users, setUsers] = useState<WithdrawalTargetRiskUserDTO[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await riskService.listWithdrawalTargetRisk({
        page,
        pageSize,
        targetType,
        keyword,
        userId,
        minDistinctUsers,
      });
      setItems(result.items);
      setTotal(result.total);
      setSelected((current) => {
        if (!current) return result.items[0] ?? null;
        return result.items.find((item) => item.target_hash === current.target_hash) ?? result.items[0] ?? null;
      });
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [keyword, minDistinctUsers, page, pageSize, targetType, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadUsers = useCallback(async (target: WithdrawalTargetRiskDTO | null) => {
    if (!target) {
      setUsers([]);
      setUsersTotal(0);
      return;
    }
    setUsersLoading(true);
    try {
      const result = await riskService.listWithdrawalTargetRiskUsers({
        targetHash: target.target_hash,
        page: 1,
        pageSize: 100,
      });
      setUsers(result.items);
      setUsersTotal(result.total);
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setUsersLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadUsers(selected);
  }, [loadUsers, selected]);

  const setFilter = useCallback((name: string, value: string) => {
    updateParams({ [name]: value || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const bulkDisable = useCallback(async (userIds: number[], reason: string) => {
    if (!selected || userIds.length === 0) return false;
    setSubmitting(true);
    try {
      const result = await riskService.bulkDisableWithdrawalTargetRiskUsers({
        targetHash: selected.target_hash,
        userIds,
        reason,
      });
      addToast('success', `已停用 ${result.disabled_user_ids.length} 个账号，跳过 ${result.skipped_user_ids.length} 个`);
      await loadUsers(selected);
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, loadUsers, selected]);

  return {
    items,
    total,
    loading,
    error,
    targetType,
    keyword,
    userId,
    minDistinctUsers,
    page,
    pageSize,
    selected,
    setSelected,
    users,
    usersTotal,
    usersLoading,
    submitting,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload: load,
    bulkDisable,
  };
}
